<script>
import BlockCard from '@/components/blocks/BlockCard';
import ContentBox from '@/components/ui/ContentBox';
import ControlBar from '@/components/layouts/dashboard/ControlBar';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import EmptyState from '@/components/ui/EmptyState';
import Selectable from '@/components/selectable/Selectable';
import Promise from 'bluebird';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Blocks',
  components: {
    BlockCard,
    ContentBox,
    DashboardLayout,
    EmptyState,
    Selectable,
    ControlBar
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Manage Blocks'),
      breadcrumbs: [],
      blocks: [],
      hasBlocks: true,
      isDeleting: false,
      lastSelectedIndex: 0,
      selection: [],
      site: {},
      statusCode: null
    };
  },
  created() {
    Promise.all([
      this.$http.get(`/sites/${this.$route.params.siteId}`),
      this.$http.get(`/sites/${this.$route.params.siteId}/blocks`, {
        params: {
          sortBy: 'name'
        }
      })
    ])
      .then(res => {
        this.site = res[0].data.site;
        this.blocks = res[1].data.blocks;
        this.hasBlocks = res[1].data.count > 0;
        this.breadcrumbs = [
          { text: this.$t('Sites'), to: { name: 'Sites' }},
          { text: this.site.name, to: { name: 'Site', params: { siteId: this.site.id }}},
          { text: this.$t('Manage Blocks') }
        ];
        this.statusCode = 200;
      })
      .catch(err => this.statusCode = err.response.status);
  },
  mounted() {
    // Watch for infinite scrolling
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeRouteLeave(to, from, next) {
    // Stop watching for infinite scrolling
    window.removeEventListener('scroll', this.handleScroll);
    next();
  },
  methods: {
    // Deletes one or more blocks
    deleteBlock(blocks) {
      const message = this.$t('This will permanently delete the selected blocks.');

      this.$confirm(message, {
        title: this.$t('Delete'),
        confirmButtonText: this.$t('Delete'),
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('Cancel'),
        callback: action => {
          if (action === 'confirm') {
            this.isDeleting = true;

            Promise.map(blocks, block => {
              return this.$http.delete(`/sites/${this.site.id}/blocks/${block.id}`);
            }, { concurrency: 10 })
              .then(res => {
                res.map(res => {
                  this.blocks = this.blocks.filter(t => t.id !== res.data.block.id);
                });
                this.hasBlocks = this.blocks.length > 0;
              })
              .catch(err => {
                this.$notify({
                  type: 'error',
                  title: this.$t('Error'),
                  message: getErrorMessage(err.response.data.code)
                });
              })
              .finally(() => this.isDeleting = false);
          }
        }
      });
    }
  }
};
</script>

<template>
  <dashboard-layout :disable-scrolling="true" :status-code="statusCode" size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Control bar -->
    <control-bar>
      <template slot="secondary">
        <el-button-group>
          <el-button
            :disabled="selection.length !== 1"
            :title="$t('Edit')"
            @click="$router.push({
              name: 'EditBlock',
              params: {
                siteId: site.id,
                blockId: selection[0].id
              }
            })"
          >
            <i class="el-icon-fa-fw el-icon-fa-edit" />
          </el-button>
          <el-button
            :disabled="selection.length === 0"
            :title="$t('Delete')"
            @click="deleteBlock(selection)"
          >
            <i class="el-icon-fa-fw el-icon-fa-trash" />
          </el-button>
        </el-button-group>

        <el-button type="primary" @click="$router.push({ name: 'NewBlock' })">
          {{ $t('New Block') }}
        </el-button>
      </template>
    </control-bar>

    <!-- Block cards -->
    <content-box v-loading.body="isDeleting" v-if="hasBlocks" class="block-cards">
      <selectable
        ref="selectable"
        v-model="selection"
        :items="blocks"
        :multiple="true"
      >
        <block-card
          v-for="block in blocks"
          :key="block.id"
          :block="block"
          :selected="$refs.selectable && $refs.selectable.isSelected(block)"
          @click="$refs.selectable.select(block, $event)"
          @dblclick.native="$router.push({
            name: 'EditBlock',
            params: {
              siteId: site.id,
              blockId: block.id
            }
          })"
        />
      </selectable>
    </content-box>

    <!-- No blocks -->
    <empty-state
      v-if="!hasBlocks"
      :title="$t('No Blocks')"
      class="empty-state"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.block-cards {
  flex: 1 1 auto;
  background-color: $--color-white;
  border-radius: .25rem;
  padding: 0;
  margin-bottom: 0;
  overflow: auto;
}
</style>
